import { useAuthStore } from "../store/authStore";

const API_BASE_URL = import.meta.env.VITE_API_URL || "";

// Log API configuration on module load
console.log("[API] Configuration:", {
  API_BASE_URL,
  VITE_API_URL: import.meta.env.VITE_API_URL,
  MODE: import.meta.env.MODE,
  DEV: import.meta.env.DEV,
  PROD: import.meta.env.PROD,
});

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  retryCount = 0,
): Promise<T> {
  const session = useAuthStore.getState().session;

  const headers: HeadersInit = {
    // Only set Content-Type if there's a body (avoids "body cannot be empty" errors on DELETE)
    ...(options.body ? { "Content-Type": "application/json" } : {}),
    ...options.headers,
  };

  if (session?.accessToken) {
    (headers as Record<string, string>)["Authorization"] =
      `Bearer ${session.accessToken}`;
  }

  const fullUrl = `${API_BASE_URL}${endpoint}`;
  console.log("[API] Request:", {
    method: options.method || "GET",
    url: fullUrl,
    hasAuth: !!session?.accessToken,
    retry: retryCount,
  });

  let response: Response;
  try {
    response = await fetch(fullUrl, { ...options, headers });
  } catch (err) {
    // Caller-driven abort (e.g. effect cleanup, navigation) — re-raise
    // the original DOMException so consumers can `signal.aborted`-check
    // without polluting the console.
    if (
      err instanceof DOMException &&
      (err.name === "AbortError" || err.name === "TimeoutError")
    ) {
      throw err;
    }
    // Network error - backend unreachable
    console.error("[API] Network error:", err);
    throw new ApiError(
      0,
      "NETWORK_ERROR",
      `Cannot connect to server (${fullUrl}). Please ensure the backend is running.`,
    );
  }

  console.log("[API] Response:", {
    url: fullUrl,
    status: response.status,
    statusText: response.statusText,
    ok: response.ok,
  });

  if (!response.ok) {
    // Handle 401 - try to refresh token before giving up
    if (response.status === 401 && retryCount === 0) {
      console.log(
        "[API] 401 Unauthorized - attempting token refresh before logout",
      );

      try {
        // Import supabase client to refresh session
        const { supabase } = await import("../lib/supabase");
        const { data, error } = await supabase.auth.refreshSession();

        if (error || !data.session) {
          console.error("[API] Token refresh failed:", error);
          console.error("[API] Error details:", {
            errorMessage: error?.message,
            errorName: error?.name,
            errorStatus: (error as any)?.status,
          });
          // Refresh failed - now we sign out
          console.log("[API] Signing out user after failed refresh");
          console.trace("[API] Signout call stack");
          useAuthStore.getState().signOut();
          throw new ApiError(
            401,
            "UNAUTHORIZED",
            "Session expired. Please sign in again.",
          );
        }

        console.log("[API] Token refresh successful, retrying request");
        // Token refreshed successfully - retry the request with new token
        // The auth store will be updated via TOKEN_REFRESHED event
        return request<T>(endpoint, options, retryCount + 1);
      } catch (err) {
        // If refresh itself fails, sign out
        console.error("[API] Token refresh error:", err);
        console.log("[API] Signing out user after refresh error");
        useAuthStore.getState().signOut();
        throw new ApiError(
          401,
          "UNAUTHORIZED",
          "Session expired. Please sign in again.",
        );
      }
    }

    // If this was already a retry, or not a 401, handle the error normally
    if (response.status === 401) {
      console.log("[API] 401 Unauthorized after retry - signing out user");
      useAuthStore.getState().signOut();
      throw new ApiError(
        401,
        "UNAUTHORIZED",
        "Session expired. Please sign in again.",
      );
    }

    // Try to parse JSON error from backend
    const text = await response.text();
    console.log("[API] Error response body:", text.substring(0, 500));
    let error: { code: string; message: string };

    try {
      error = JSON.parse(text);
    } catch {
      // Not JSON - likely a proxy error (backend unreachable)
      // Check for common proxy error patterns
      if (
        response.status === 500 ||
        response.status === 502 ||
        response.status === 503
      ) {
        throw new ApiError(
          response.status,
          "BACKEND_UNAVAILABLE",
          "Cannot connect to server. Please ensure the backend is running.",
        );
      }
      error = { code: "UNKNOWN_ERROR", message: "An unknown error occurred" };
    }
    // Handle different error formats from backend
    const code = error.code || (error as any).error || "ERROR";
    const message =
      error.message ||
      (error as any).details ||
      (error as any).error ||
      "An error occurred";
    throw new ApiError(response.status, code, message);
  }

  // Handle empty responses (e.g., 204 No Content or DELETE with no body)
  const contentLength = response.headers.get("content-length");
  if (response.status === 204 || contentLength === "0") {
    return {} as T;
  }

  // Try to parse JSON, return empty object if body is empty
  const text = await response.text();
  if (!text) {
    return {} as T;
  }

  return JSON.parse(text);
}

export const api = {
  get: <T>(endpoint: string, options: { signal?: AbortSignal } = {}) =>
    request<T>(endpoint, { method: "GET", signal: options.signal }),

  post: <T>(endpoint: string, data?: unknown) =>
    request<T>(endpoint, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    }),

  patch: <T>(endpoint: string, data?: unknown) =>
    request<T>(endpoint, {
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: <T>(endpoint: string, data?: unknown) => {
    const body = data ? JSON.stringify(data) : undefined;
    return request<T>(endpoint, { method: "PUT", body });
  },

  delete: <T>(endpoint: string) => request<T>(endpoint, { method: "DELETE" }),
};

/**
 * Raw fetch with API_BASE_URL prepended.
 * Use this for requests that need custom headers, methods, or body handling
 * that the api helper methods don't support (e.g., FormData uploads).
 */
export async function fetchWithBase(
  endpoint: string,
  options: RequestInit = {},
): Promise<Response> {
  const session = useAuthStore.getState().session;

  const headers: HeadersInit = { ...options.headers };

  if (session?.accessToken) {
    (headers as Record<string, string>)["Authorization"] =
      `Bearer ${session.accessToken}`;
  }

  return fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers });
}

/**
 * Get the API base URL for use in direct fetch calls
 */
export function getApiBaseUrl(): string {
  return API_BASE_URL;
}

/**
 * Compress an image file to reduce size for LLM vision API.
 * Resizes to max 1024px on longest side and compresses to JPEG.
 */
async function compressImage(
  file: File,
  maxSize = 1024,
  quality = 0.8,
): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    img.onload = () => {
      // Calculate new dimensions (max 1024px on longest side)
      let { width, height } = img;
      if (width > height) {
        if (width > maxSize) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;

      if (!ctx) {
        reject(new Error("Failed to get canvas context"));
        return;
      }

      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error("Failed to compress image"));
            return;
          }

          // Create new file with compressed data
          const compressedFile = new File(
            [blob],
            file.name.replace(/\.[^.]+$/, ".jpg"),
            { type: "image/jpeg" },
          );

          console.log(
            `[API] Image compressed: ${file.size} -> ${compressedFile.size} bytes (${Math.round((compressedFile.size / file.size) * 100)}%)`,
          );
          resolve(compressedFile);
        },
        "image/jpeg",
        quality,
      );
    };

    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Upload a file to the server.
 * Images are automatically compressed before upload.
 * Returns the uploaded file metadata including URL.
 */
export async function uploadFile<T>(
  endpoint: string,
  file: File,
  retryCount = 0,
): Promise<T> {
  const session = useAuthStore.getState().session;

  // Compress images before upload
  let fileToUpload = file;
  if (file.type.startsWith("image/")) {
    try {
      fileToUpload = await compressImage(file);
    } catch (err) {
      console.warn("[API] Image compression failed, uploading original:", err);
    }
  }

  const formData = new FormData();
  formData.append("file", fileToUpload);

  const headers: HeadersInit = {};
  if (session?.accessToken) {
    headers["Authorization"] = `Bearer ${session.accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!response.ok) {
    // Handle 401 - try to refresh token before giving up
    if (response.status === 401 && retryCount === 0) {
      console.log("[API] Upload 401 - attempting token refresh");
      try {
        const { supabase } = await import("../lib/supabase");
        const { data, error } = await supabase.auth.refreshSession();

        if (!error && data.session) {
          console.log("[API] Token refresh successful, retrying upload");
          return uploadFile<T>(endpoint, file, retryCount + 1);
        }
      } catch (err) {
        console.error("[API] Token refresh error during upload:", err);
      }
      // Refresh failed - sign out
      console.log("[API] Signing out user after failed upload refresh");
      useAuthStore.getState().signOut();
      throw new ApiError(
        401,
        "UNAUTHORIZED",
        "Session expired. Please sign in again.",
      );
    }

    const error = await response
      .json()
      .catch(() => ({ code: "UPLOAD_ERROR", message: "File upload failed" }));
    throw new ApiError(
      response.status,
      error.code || "UPLOAD_ERROR",
      error.message,
    );
  }

  return response.json();
}

// Streaming API for generation
export async function* streamRequest(
  endpoint: string,
  data: unknown,
  retryCount = 0,
): AsyncGenerator<{
  type: string;
  content?: string;
  message?: string;
  [key: string]: unknown;
}> {
  const session = useAuthStore.getState().session;

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(session?.accessToken
        ? { Authorization: `Bearer ${session.accessToken}` }
        : {}),
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    // Handle 401 - try to refresh token before giving up
    if (response.status === 401 && retryCount === 0) {
      console.log("[API] Stream 401 - attempting token refresh");
      try {
        const { supabase } = await import("../lib/supabase");
        const { data: refreshData, error } =
          await supabase.auth.refreshSession();

        if (!error && refreshData.session) {
          console.log("[API] Token refresh successful, retrying stream");
          yield* streamRequest(endpoint, data, retryCount + 1);
          return;
        }
      } catch (err) {
        console.error("[API] Token refresh error during stream:", err);
      }
      // Refresh failed - sign out
      console.log("[API] Signing out user after failed stream refresh");
      useAuthStore.getState().signOut();
      throw new ApiError(
        401,
        "UNAUTHORIZED",
        "Session expired. Please sign in again.",
      );
    }

    const error = await response
      .json()
      .catch(() => ({
        code: "STREAM_ERROR",
        message: "Stream request failed",
      }));
    // Handle different error formats from backend
    const code = error.code || error.error || "STREAM_ERROR";
    const message =
      error.message || error.details || error.error || "Stream request failed";
    throw new ApiError(response.status, code, message);
  }

  const reader = response.body?.getReader();
  const decoder = new TextDecoder();

  if (!reader) {
    throw new ApiError(500, "NO_READER", "No response body reader");
  }

  let buffer = "";
  let currentEventType = "";

  while (true) {
    const { done, value } = await reader.read();

    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (line.startsWith("event: ")) {
        // Capture the event type for the next data line
        currentEventType = line.slice(7).trim();
      } else if (line.startsWith("data: ")) {
        try {
          const eventData = JSON.parse(line.slice(6));
          // Include the event type in the yielded data
          yield {
            type: currentEventType || eventData.type || "message",
            ...eventData,
          };
          currentEventType = "";
        } catch {
          // Ignore malformed JSON
        }
      }
    }
  }
}
